import { cur } from '@/lib/currency';
import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Monitor, Users, Calculator, Package, TrendingUp, BarChart3,
  Bell, Factory, ShoppingCart, Clock, Sun, Coffee, Moon,
  Check, ArrowRight, Zap, Shield, FileText, ChevronRight,
  Activity, PieChart, Layers
} from 'lucide-react';

/* ────────────────────────────────────────────
   1. ANIMATED COUNTER
   ──────────────────────────────────────────── */
export function AnimatedCounter({ value, suffix = '', prefix = '', duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [count, setCount] = useState(0);
  const numericValue = parseInt(value.replace(/[^0-9.]/g, ''), 10) || 0;

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const end = numericValue;
    const stepTime = Math.max(Math.floor((duration * 1000) / end), 16);
    const increment = Math.max(1, Math.floor(end / (duration * 60)));
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, [inView, numericValue, duration]);

  return (
    <span ref={ref}>
      {prefix}{inView ? count : 0}{suffix}
    </span>
  );
}

/* ────────────────────────────────────────────
   2. MINI ANIMATED DASHBOARD
   ──────────────────────────────────────────── */
export function MiniDashboard() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  const bars = [65, 85, 45, 92, 70, 55, 80];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div ref={ref} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 w-full" data-testid="mini-dashboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#0055FF] to-[#007AFF] flex items-center justify-center">
            <BarChart3 className="w-3 h-3 text-white" />
          </div>
          <span className="text-[11px] font-semibold text-[#1D1D1F]">Revenue</span>
        </div>
        <motion.span
          className="text-[11px] font-bold text-success"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.5 }}>
          +24.5%
        </motion.span>
      </div>
      <div className="flex items-end gap-1.5 h-16">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-end justify-end gap-1">
            <motion.div
              className="w-full rounded-sm bg-gradient-to-t from-[#0055FF] to-[#4D8DFF]"
              style={{ minHeight: 2 }}
              initial={{ height: 2 }}
              animate={inView ? { height: Math.round(h * 0.56) } : {}}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            />
            <span className="text-[8px] text-[#86868B]">{days[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   3. MINI ANIMATED POS
   ──────────────────────────────────────────── */
export function MiniPOS() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  const items = [
    { name: 'Espresso', price: '12.00', delay: 0.4 },
    { name: 'Croissant', price: '8.50', delay: 0.8 },
    { name: 'Salad Bowl', price: '24.00', delay: 1.2 },
  ];

  return (
    <div ref={ref} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 w-full" data-testid="mini-pos">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
          <ShoppingCart className="w-3 h-3 text-white" />
        </div>
        <span className="text-[11px] font-semibold text-[#1D1D1F]">Quick Order</span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <motion.div key={i}
            className="flex items-center justify-between py-1 px-2 bg-[#F9F9FB] rounded-md"
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: item.delay, duration: 0.4 }}>
            <span className="text-[10px] text-[#1D1D1F] font-medium">{item.name}</span>
            <span className="text-[10px] text-[#86868B]">{cur()} {item.price}</span>
          </motion.div>
        ))}
      </div>
      <motion.div
        className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 1.6 }}>
        <span className="text-[10px] font-bold text-[#1D1D1F]">Total</span>
        <span className="text-[11px] font-bold text-[#0040CC]">{cur()} 44.50</span>
      </motion.div>
      <motion.div
        className="mt-2 w-full h-6 bg-gradient-to-r from-[#0055FF] to-[#007AFF] rounded-md flex items-center justify-center"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={inView ? { scale: 1, opacity: 1 } : {}}
        transition={{ delay: 1.8, type: 'spring', stiffness: 200 }}>
        <span className="text-[9px] font-bold text-white">Charge SR 44.50</span>
      </motion.div>
    </div>
  );
}

/* ────────────────────────────────────────────
   4. MINI ANIMATED NOTIFICATIONS
   ──────────────────────────────────────────── */
export function MiniNotifications() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  const notifs = [
    { icon: Users, text: 'Leave request from Ahmed', time: '2m ago', color: 'bg-info', delay: 0.5 },
    { icon: Package, text: 'Low stock: Flour (5 kg left)', time: '15m ago', color: 'bg-warning', delay: 0.9 },
    { icon: TrendingUp, text: 'Revenue target hit: 120%', time: '1h ago', color: 'bg-success', delay: 1.3 },
  ];

  return (
    <div ref={ref} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 w-full" data-testid="mini-notifications">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center">
            <Bell className="w-3 h-3 text-white" />
          </div>
          <span className="text-[11px] font-semibold text-[#1D1D1F]">Notifications</span>
        </div>
        <motion.div
          className="w-4 h-4 rounded-full bg-error flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={inView ? { scale: [0, 1.3, 1] } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}>
          <span className="text-[7px] font-bold text-white">3</span>
        </motion.div>
      </div>
      <div className="space-y-1.5">
        {notifs.map((n, i) => (
          <motion.div key={i}
            className="flex items-start gap-2 py-1.5 px-2 bg-[#F9F9FB] rounded-md"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ delay: n.delay, duration: 0.4, type: 'spring' }}>
            <div className={`w-4 h-4 rounded-full ${n.color} flex items-center justify-center mt-0.5 shrink-0`}>
              <n.icon className="w-2 h-2 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] text-[#1D1D1F] font-medium truncate">{n.text}</p>
              <p className="text-[8px] text-[#86868B]">{n.time}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   5. MINI FACTORY PRODUCTION LINE
   ──────────────────────────────────────────── */
export function MiniFactory() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  const stages = [
    { label: 'Raw Materials', pct: 100, color: 'from-cyan-400 to-cyan-500' },
    { label: 'Production', pct: 78, color: 'from-blue-400 to-blue-500' },
    { label: 'Quality Check', pct: 65, color: 'from-violet-400 to-violet-500' },
    { label: 'Completed', pct: 52, color: 'from-emerald-400 to-emerald-500' },
  ];

  return (
    <div ref={ref} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 w-full" data-testid="mini-factory">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
          <Factory className="w-3 h-3 text-white" />
        </div>
        <span className="text-[11px] font-semibold text-[#1D1D1F]">Production Line</span>
      </div>
      <div className="space-y-2">
        {stages.map((s, i) => (
          <div key={s.label}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[9px] text-[#86868B] font-medium">{s.label}</span>
              <motion.span
                className="text-[9px] font-bold text-[#1D1D1F]"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ delay: 0.5 + i * 0.3 }}>
                {s.pct}%
              </motion.span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full bg-gradient-to-r ${s.color}`}
                initial={{ width: 0 }}
                animate={inView ? { width: `${s.pct}%` } : {}}
                transition={{ duration: 0.8, delay: 0.4 + i * 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   6. MINI ANALYTICS CHART (Line drawing)
   ──────────────────────────────────────────── */
export function MiniAnalytics() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });

  const path = "M 0 40 C 15 35, 25 20, 40 25 C 55 30, 65 10, 80 15 C 95 20, 105 5, 120 8";
  const areaPath = path + " L 120 50 L 0 50 Z";

  return (
    <div ref={ref} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 w-full" data-testid="mini-analytics">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Activity className="w-3 h-3 text-white" />
          </div>
          <span className="text-[11px] font-semibold text-[#1D1D1F]">Analytics</span>
        </div>
        <motion.div
          className="flex items-center gap-1"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.5 }}>
          <TrendingUp className="w-3 h-3 text-success" />
          <span className="text-[10px] font-bold text-success">+18%</span>
        </motion.div>
      </div>
      <svg viewBox="0 0 120 50" className="w-full h-12">
        <motion.path
          d={areaPath}
          fill="url(#areaGrad)"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 0.3 } : {}}
          transition={{ delay: 0.8, duration: 0.5 }}
        />
        <motion.path
          d={path}
          fill="none"
          stroke="url(#lineGrad)"
          strokeWidth="2"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 1.5, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        />
        <motion.circle
          cx="120" cy="8" r="3"
          fill="#10B981"
          initial={{ scale: 0 }}
          animate={inView ? { scale: [0, 1.5, 1] } : {}}
          transition={{ delay: 1.8, duration: 0.4 }}
        />
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="120" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="50" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <div className="flex items-center justify-between mt-2">
        {['Jan', 'Feb', 'Mar', 'Apr'].map(m => (
          <span key={m} className="text-[8px] text-[#86868B]">{m}</span>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   7. HOW IT WORKS — 4-Step Walkthrough
   ──────────────────────────────────────────── */
const STEPS = [
  {
    num: '01',
    title: 'Set Up Your Business',
    desc: 'Create your company, add branches, and configure your industry type in under 5 minutes.',
    icon: Zap,
    color: 'from-[#0055FF] to-[#007AFF]',
  },
  {
    num: '02',
    title: 'Manage Operations',
    desc: 'Process sales, track inventory, manage employees, and run your factory — all from one dashboard.',
    icon: Layers,
    color: 'from-blue-500 to-indigo-600',
  },
  {
    num: '03',
    title: 'Track & Analyze',
    desc: 'Real-time analytics, production yield tracking, AI-powered insights, and automated notifications.',
    icon: PieChart,
    color: 'from-emerald-500 to-teal-600',
  },
  {
    num: '04',
    title: 'Scale & Grow',
    desc: 'Add branches in new countries, onboard teams, and unlock advanced modules as you grow.',
    icon: TrendingUp,
    color: 'from-violet-500 to-purple-600',
  },
];

export function HowItWorks() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <div ref={ref} className="grid grid-cols-1 md:grid-cols-4 gap-6 relative" data-testid="how-it-works">
      {/* Connecting line (desktop only) */}
      <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-[2px] bg-gray-100 z-0">
        <motion.div
          className="h-full bg-gradient-to-r from-[#0055FF] via-blue-500 via-emerald-500 to-violet-500"
          initial={{ width: 0 }}
          animate={inView ? { width: '100%' } : {}}
          transition={{ duration: 2, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </div>
      {STEPS.map((step, i) => (
        <motion.div key={step.num}
          className="relative z-10 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 + i * 0.2 }}>
          <motion.div
            className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg mb-5`}
            whileHover={{ scale: 1.08, rotate: 3 }}
            transition={{ type: 'spring', stiffness: 300 }}>
            <step.icon className="w-8 h-8 text-white" />
          </motion.div>
          <span className="text-[11px] font-bold text-[#86868B] tracking-wider">{step.num}</span>
          <h3 className="text-[15px] font-semibold text-[#1D1D1F] mt-1 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.title}</h3>
          <p className="text-[13px] text-[#86868B] leading-relaxed max-w-[200px] mx-auto">{step.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────
   8. YOUR DAY WITH SMARTO — Timeline
   ──────────────────────────────────────────── */
const TIMELINE = [
  {
    time: '8:00 AM',
    label: 'Morning',
    icon: Coffee,
    title: 'Check Your Dashboard',
    desc: 'Overnight sales, pending approvals, and stock alerts — everything at a glance.',
    color: 'from-[#4D8DFF] to-[#0055FF]',
    Demo: MiniDashboard,
  },
  {
    time: '12:00 PM',
    label: 'Midday',
    icon: Sun,
    title: 'Manage Operations',
    desc: 'Process orders, approve leave requests, and monitor production in real-time.',
    color: 'from-blue-400 to-indigo-500',
    Demo: MiniPOS,
  },
  {
    time: '6:00 PM',
    label: 'Evening',
    icon: Moon,
    title: 'Review & Optimize',
    desc: 'Analyze performance, track yields, and plan tomorrow with AI-powered insights.',
    color: 'from-violet-400 to-purple-500',
    Demo: MiniAnalytics,
  },
];

export function YourDayTimeline() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <div ref={ref} className="space-y-12 md:space-y-0 md:grid md:grid-cols-3 md:gap-8" data-testid="your-day-timeline">
      {TIMELINE.map((item, i) => (
        <motion.div key={item.time}
          className="relative"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 + i * 0.25 }}>
          {/* Time badge */}
          <div className="flex items-center gap-3 mb-4">
            <motion.div
              className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md`}
              whileHover={{ scale: 1.1, rotate: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}>
              <item.icon className="w-5 h-5 text-white" />
            </motion.div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#86868B]">{item.label}</span>
              <p className="text-[13px] font-semibold text-[#1D1D1F]">{item.time}</p>
            </div>
          </div>
          <h3 className="text-[17px] font-semibold text-[#1D1D1F] mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
          <p className="text-[13px] text-[#86868B] leading-relaxed mb-4">{item.desc}</p>
          {/* Animated Demo */}
          <item.Demo />
        </motion.div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────
   9. INTERACTIVE FEATURE PLAYGROUND
   ──────────────────────────────────────────── */
const PLAYGROUND_CARDS = [
  { title: 'Smart Dashboard', desc: 'Real-time KPIs update live', Demo: MiniDashboard, gradient: 'from-[#F0F5FF] to-[#F0F5FF]', border: 'border-[#DCE8FF]/50' },
  { title: 'Point of Sale', desc: 'Process orders instantly', Demo: MiniPOS, gradient: 'from-blue-50 to-indigo-50', border: 'border-info/30' },
  { title: 'Notifications', desc: 'Never miss an alert', Demo: MiniNotifications, gradient: 'from-red-50 to-rose-50', border: 'border-error/30' },
  { title: 'Factory Line', desc: 'Track every production stage', Demo: MiniFactory, gradient: 'from-cyan-50 to-blue-50', border: 'border-cyan-100/50' },
];

export function FeaturePlayground() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="feature-playground">
      {PLAYGROUND_CARDS.map((card, i) => (
        <motion.div key={card.title}
          className={`rounded-2xl bg-gradient-to-br ${card.gradient} border ${card.border} p-5 hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 cursor-default`}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 + i * 0.12 }}>
          <h3 className="text-[14px] font-semibold text-[#1D1D1F] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{card.title}</h3>
          <p className="text-[12px] text-[#86868B] mb-3">{card.desc}</p>
          <card.Demo />
        </motion.div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────
   10. FLOATING PARTICLES (Hero decoration)
   ──────────────────────────────────────────── */
export function FloatingParticles() {
  const particles = [
    { size: 6, x: '10%', y: '20%', delay: 0, dur: 6, color: 'bg-[#7FB0FF]/30' },
    { size: 4, x: '80%', y: '15%', delay: 1, dur: 8, color: 'bg-blue-300/30' },
    { size: 8, x: '70%', y: '60%', delay: 2, dur: 7, color: 'bg-[#7FB0FF]/20' },
    { size: 5, x: '20%', y: '70%', delay: 0.5, dur: 9, color: 'bg-emerald-300/20' },
    { size: 3, x: '90%', y: '40%', delay: 3, dur: 5, color: 'bg-violet-300/20' },
    { size: 7, x: '50%', y: '80%', delay: 1.5, dur: 6.5, color: 'bg-[#BBD2FF]/20' },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {particles.map((p, i) => (
        <motion.div key={i}
          className={`absolute rounded-full ${p.color}`}
          style={{ width: p.size, height: p.size, left: p.x, top: p.y }}
          animate={{ y: [-10, 10, -10], x: [-5, 5, -5], opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}
